import { useId } from 'react'
import { Stack, TableCell, TextField, Typography } from '@mui/material'
import { LuminaPersonUpdate, LuminaProcessPerson } from './services'

export const validPerformance = (value: string): boolean => {
  const score: string = value.trim()
  return (
    score === '' ||
    (/^\d{1,2}([.,]\d{1,2})?$/.test(score) &&
      Number(score.replace(',', '.')) >= 1 &&
      Number(score.replace(',', '.')) <= 10)
  )
}

export const getPersonUpdates = (
  people: Array<Pick<LuminaProcessPerson, 'user_id' | 'cargo' | 'desempeno'>>,
  cargos: Record<number, string>,
  scores: Record<number, string>
): LuminaPersonUpdate[] =>
  people.flatMap((person) => {
    const cargo: string = (cargos[person.user_id] ?? person.cargo ?? '').trim()
    const score: string = (scores[person.user_id] ?? (person.desempeno == null ? '' : String(person.desempeno))).trim()
    const value: number | null = score === '' ? null : Number(score.replace(',', '.'))
    const update: LuminaPersonUpdate = { user_id: person.user_id }
    if (cargo !== (person.cargo || '').trim()) update.cargo = cargo
    if (value !== (person.desempeno == null ? null : Number(person.desempeno))) update.desempeno = value
    return update.cargo !== undefined || update.desempeno !== undefined ? [update] : []
  })

interface Props {
  name: string
  cargo: string
  performance: string
  disabled?: boolean
  onCargoChange: (value: string) => void
  onPerformanceChange: (value: string) => void
}

export default function PersonFieldsCells({
  name,
  cargo,
  performance,
  disabled,
  onCargoChange,
  onPerformanceChange,
}: Props) {
  const hintId: string = useId()
  const valid: boolean = validPerformance(performance)
  const hint: string = !valid
    ? 'De 1 a 10, con hasta dos decimales.'
    : !performance.trim()
    ? 'Necesario para el 9Box.'
    : ''
  return (
    <>
      <TableCell sx={{ minWidth: 220 }}>
        <TextField
          label="Cargo"
          size="small"
          fullWidth
          value={cargo}
          disabled={disabled}
          onChange={(event) => onCargoChange(event.target.value)}
          inputProps={{ maxLength: 500, 'aria-label': `Cargo de ${name}` }}
        />
      </TableCell>
      <TableCell sx={{ width: 300, maxWidth: 300, verticalAlign: 'middle' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.25}>
          <TextField
            label="1–10"
            size="small"
            sx={{ width: 90, flexShrink: 0 }}
            value={performance}
            disabled={disabled}
            onChange={(event) => onPerformanceChange(event.target.value)}
            inputProps={{
              inputMode: 'decimal',
              maxLength: 10,
              'aria-label': `Desempeño de ${name}`,
              'aria-describedby': hint ? hintId : undefined,
            }}
            error={!valid}
          />
          {hint && (
            <Typography
              id={hintId}
              variant="caption"
              color={!valid ? 'error' : 'text.secondary'}
              sx={{ lineHeight: 1.3 }}
            >
              {hint}
            </Typography>
          )}
        </Stack>
      </TableCell>
    </>
  )
}
